import {
	createFileRoute,
	Link,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { ArrowRight, CheckCircle, Loader2, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandCard } from "@/components/brand/brand-card";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PublicHeader } from "@/components/public-layout";
import { useSession } from "@/hooks/auth-hooks";
import { claimCheckout } from "@/lib/claim-checkout";
import { fireGoldenConfetti } from "@/lib/confetti";
import { parseInternalRedirect } from "@/lib/internal-redirect";
import { getPublicPlanName, normalizePublicPlan } from "@/lib/plans";
import { noIndexMeta } from "@/lib/seo";

type ConversionWindow = Window & {
	dataLayer?: Record<string, unknown>[];
	gtag?: (...args: unknown[]) => void;
};

export const Route = createFileRoute("/upgrade-success")({
	validateSearch: (search: Record<string, unknown>) => ({
		plan: (search.plan as string) || "pro",
		redirect: parseInternalRedirect(search.redirect),
		checkoutSessionId:
			typeof search.checkoutSessionId === "string"
				? search.checkoutSessionId
				: undefined,
		claimToken:
			typeof search.claimToken === "string" ? search.claimToken : undefined,
	}),
	component: UpgradeSuccessPage,
	head: () => ({
		meta: [{ title: "AgentReacher | Upgrade complete" }, ...noIndexMeta()],
	}),
});

function UpgradeSuccessPage() {
	const navigate = useNavigate();
	const { plan, redirect, checkoutSessionId, claimToken } = useSearch({
		from: "/upgrade-success",
	});
	const { user, isPending } = useSession();
	const [claimStatus, setClaimStatus] = useState<
		"idle" | "claiming" | "claimed" | "error"
	>(checkoutSessionId && claimToken ? "idle" : "claimed");
	const [claimError, setClaimError] = useState<string | null>(null);
	const planLabel = getPublicPlanName(normalizePublicPlan(plan));
	const claimRedirect = useMemo(() => {
		if (typeof window === "undefined") return "/upgrade-success";
		return `${window.location.pathname}${window.location.search}`;
	}, []);
	const hasClaimParams = !!checkoutSessionId && !!claimToken;

	useEffect(() => {
		if (!hasClaimParams || isPending || !user || claimStatus !== "idle") return;

		setClaimStatus("claiming");
		claimCheckout({ checkoutSessionId, claimToken })
			.then(() => {
				setClaimStatus("claimed");
				setClaimError(null);
			})
			.catch((error) => {
				setClaimStatus("error");
				setClaimError(
					error instanceof Error
						? error.message
						: "Failed to activate your subscription",
				);
			});
	}, [
		claimStatus,
		claimToken,
		checkoutSessionId,
		hasClaimParams,
		isPending,
		user,
	]);

	useEffect(() => {
		if (claimStatus !== "claimed") return;

		const confettiKey = `ar_upgrade_confetti_fired_${plan}`;
		if (!sessionStorage.getItem(confettiKey)) {
			sessionStorage.setItem(confettiKey, "1");
			window.setTimeout(() => fireGoldenConfetti(), 120);
		}

		const key = `ga_purchase_fired_${plan}`;
		if (sessionStorage.getItem(key)) return;
		sessionStorage.setItem(key, "1");

		const priceMap: Record<string, number> = {
			pro: 79,
			"pro-yearly": 756,
			max: 199,
			"max-yearly": 1908,
		};
		const value = priceMap[plan] ?? 79;

		const transactionId = crypto.randomUUID();
		const purchaseEvent = {
			transaction_id: transactionId,
			value,
			currency: "EUR",
			items: [{ item_name: plan }],
		};
		const conversionEvent = {
			send_to: "AW-17133317133/2_SHCMi5qZgcEI3Y5uk_",
			transaction_id: transactionId,
		};
		const conversionWindow = window as ConversionWindow;
		if (typeof conversionWindow.gtag === "function") {
			conversionWindow.gtag("event", "purchase", purchaseEvent);
			conversionWindow.gtag("event", "conversion", conversionEvent);
			return;
		}

		conversionWindow.dataLayer = conversionWindow.dataLayer || [];
		conversionWindow.dataLayer.push({ event: "purchase", ...purchaseEvent });
		conversionWindow.dataLayer.push({
			event: "conversion",
			...conversionEvent,
		});
	}, [claimStatus, plan]);

	if (isPending) {
		return <LoadingScreen />;
	}

	if (hasClaimParams && !user) {
		return (
			<div className="min-h-svh bg-background">
				<PublicHeader />

				<main className="mx-auto max-w-lg px-4 py-16">
					<BrandCard variant="elevated" padding="lg" className="text-center">
						<div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
							<CheckCircle className="size-8" />
						</div>

						<h1 className="text-3xl font-extrabold text-foreground">
							Create your account to activate your subscription
						</h1>
						<p className="mt-4 text-muted-foreground">
							Your {planLabel} subscription is ready. Sign up or log in to
							connect it to your AgentReacher account.
						</p>

						<div className="mt-8 space-y-3">
							<Link
								to="/signup"
								search={{ redirect: claimRedirect }}
								className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
							>
								Create account
								<ArrowRight className="size-4" />
							</Link>
							<Link
								to="/login"
								search={{ redirect: claimRedirect }}
								className="inline-flex w-full items-center justify-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted/60"
							>
								Log in instead
							</Link>
						</div>
					</BrandCard>
				</main>
			</div>
		);
	}

	if (hasClaimParams && claimStatus === "claiming") {
		return (
			<div className="min-h-svh bg-background">
				<PublicHeader />
				<main className="mx-auto max-w-lg px-4 py-16">
					<BrandCard variant="elevated" padding="lg" className="text-center">
						<Loader2 className="mx-auto mb-6 size-10 animate-spin text-primary" />
						<h1 className="text-3xl font-extrabold text-foreground">
							Activating your subscription
						</h1>
						<p className="mt-4 text-muted-foreground">
							We are connecting your Stripe checkout to your AgentReacher
							account.
						</p>
					</BrandCard>
				</main>
			</div>
		);
	}

	if (hasClaimParams && claimStatus === "error") {
		return (
			<div className="min-h-svh bg-background">
				<PublicHeader />
				<main className="mx-auto max-w-lg px-4 py-16">
					<BrandCard variant="elevated" padding="lg" className="text-center">
						<h1 className="text-3xl font-extrabold text-foreground">
							We could not activate your subscription
						</h1>
						<p className="mt-4 text-muted-foreground">
							{claimError ?? "Please try again or contact support."}
						</p>
						<BrandButton
							className="mt-8 w-full"
							onClick={() => setClaimStatus("idle")}
						>
							Try again
						</BrandButton>
					</BrandCard>
				</main>
			</div>
		);
	}

	return (
		<div className="min-h-svh bg-background">
			<PublicHeader />

			<main className="max-w-lg mx-auto px-4 py-16">
				<BrandCard variant="elevated" padding="lg" className="text-center">
					<div className="mx-auto mb-6 w-20 h-20 rounded-full bg-primary flex items-center justify-center shadow-lg 0">
						<CheckCircle className="w-10 h-10 text-primary-foreground" />
					</div>

					<div className="flex items-center justify-center gap-2 mb-4">
						<Sparkles className="w-6 h-6 text-primary" />
						<h1 className="text-3xl font-extrabold text-foreground">
							{`${planLabel} plan activated`}
						</h1>
						<Sparkles className="w-6 h-6 text-primary" />
					</div>

					<p className="text-muted-foreground mb-8">
						Your subscription is active and your account is ready to use.
					</p>

					<div className="space-y-3">
						<BrandButton
							className="w-full"
							onClick={() => {
								if (redirect) {
									window.location.assign(redirect);
									return;
								}
								navigate({ to: "/dashboard" });
							}}
						>
							{redirect ? "Continue setup" : "Go to dashboard"}
							<ArrowRight className="w-4 h-4 ml-2" />
						</BrandButton>

						<button
							type="button"
							onClick={() => navigate({ to: "/dashboard/billing" })}
							className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
						>
							Manage subscription
						</button>
					</div>
				</BrandCard>
			</main>
		</div>
	);
}
